/**
 * Conversations API Service
 * Handles all conversation-related API calls
 * 
 * Flow:
 * 1. POST /ai/conversations - Create new conversation (triggers AI workflow)
 * 2. Subscribe to WebSocket for real-time updates
 * 3. PUT /ai/conversations/:id - Send follow-up message (resumes AI workflow)
 */

import { apiClient } from './client'
import { Conversation, SalesProduct } from '../../types'

export interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface ConversationsListResponse {
  conversations: Conversation[]
  meta: PaginationMeta
}

interface ConversationResponse {
  conversation: Conversation
}

export interface ConversationsQueryParams {
  page?: number
  per_page?: number
  q?: string
}

/**
 * Request to create a new conversation
 * This triggers the AI workflow in the backend
 */
export interface CreateConversationRequest {
  advertiser_id: number
  query: string
  brand_kit_id?: number
  workflow?: string
  campaign_id?: number
  content_size?: string[]
  /**
   * Typed reference-asset URL buckets. Mirror the shape of
   * `response_state.input_reference_data.{input_image_data,
   * input_video_data,input_audio_data}` on the way out, so a round-trip
   * (attach a video → BE persists it → response returns `input_video_data`)
   * uses the same field name on both ends.
   *
   * Only used for JSON-body requests that pass pre-uploaded URLs; the
   * multipart send path (see `Home.tsx` / `Campaigns.tsx`) appends actual
   * File blobs under `conversation[reference_*][]` keys directly.
   */
  reference_images?: string[]
  reference_videos?: string[]
  reference_audio?: string[]
  [key: string]: unknown
}

/**
 * Request to continue an existing conversation
 * Used for follow-up messages after initial response
 */
export interface ContinueConversationRequest {
  query: string
  status: 'input_received'
}

/**
 * Legacy update request for other conversation updates
 */
export interface UpdateConversationRequest {
  query?: string
  chat_history?: Array<{ role: string; content: string }>
  status?: string | number
  target_workflows?: string[]
  current_workflow_index?: number
  response_state?: Record<string, any>
  advertiser_id?: number
}

/**
 * Selected template for ad unit creation
 */
export interface SelectedTemplate {
  content_type: string
  sizes: string[]
}

/**
 * Request to create ad units from selected templates
 */
export interface CreateAdUnitsRequest {
  query: string
  status: 'input_received'
  selected_templates: SelectedTemplate[]
}

/**
 * Creative item for sync creatives request
 */
export interface SyncCreativeItem {
  creative_id: string
  name: string
  format_id: {
    agent_url: string
    id: string
  }
  assets: Record<string, unknown>
  enzymic_ad_unit_id: number | null
  enzymic_campaign_id: number | null
}

/**
 * One creative-to-package assignment. A creative that targets multiple
 * packages is expressed as multiple entries — one per package.
 */
export interface CreativeAssignment {
  creative_id: string
  package_id: string
}

/**
 * Request to sync creatives with media buy
 *
 * Payload structure:
 * - query: "sync_creatives"
 * - status: "input_received"
 * - selected_templates: Array of { content_type, sizes } extracted from format_ids
 * - creatives: Array of creative items with format_id, name, etc.
 * - assignments: Array of { creative_id, package_id } pairs (one row per pairing)
 */
export interface SyncCreativesRequest {
  selected_templates: SelectedTemplate[]
  creatives: SyncCreativeItem[]
  assignments: CreativeAssignment[]
}

function buildQueryString(params: ConversationsQueryParams): string {
  const queryParams = new URLSearchParams()
  if (params.page) queryParams.append('page', params.page.toString())
  if (params.per_page) queryParams.append('per_page', params.per_page.toString())
  if (params.q) queryParams.append('q', params.q)
  const queryString = queryParams.toString()
  return queryString ? `?${queryString}` : ''
}

export const conversationApi = {
  /**
   * Get conversations for the current user with pagination
   * Endpoint: /api/v1/ai/conversations (base URL already has /api/v1)
   */
  getConversations: async (params: ConversationsQueryParams = {}): Promise<{ conversations: Conversation[], meta: PaginationMeta }> => {
    const queryString = buildQueryString(params)
    const response = await apiClient.get<ConversationsListResponse>(
      `/ai/conversations${queryString}`,
      { skipRoleRouting: true }
    )
    return {
      conversations: response.conversations || [],
      meta: response.meta || { current_page: 1, total_pages: 1, total_count: 0, per_page: 20 }
    }
  },

  /**
   * Get a specific conversation by ID
   * Returns full conversation data including chat_history, response_state, campaign
   */
  getConversation: async (id: number): Promise<Conversation> => {
    const response = await apiClient.get<ConversationResponse>(
      `/ai/conversations/${id}`,
      { skipRoleRouting: true }
    )
    return response.conversation
  },

  /**
   * Create a new conversation and trigger AI workflow
   * 
   * Flow:
   * 1. POST creates conversation with status: "pending"
   * 2. Backend triggers Ai::WorkflowTriggerJob
   * 3. AI processes and sends callbacks
   * 4. WebSocket broadcasts updates to FE
   * 
   * After calling this, subscribe to WebSocket with the returned conversation ID
   */
  createConversation: async (data: CreateConversationRequest | FormData): Promise<Conversation> => {
    if (data instanceof FormData) {
      const response = await apiClient.postFormData<ConversationResponse>('/ai/conversations', data, { skipRoleRouting: true })
      return response.conversation
    }
    const response = await apiClient.post<ConversationResponse>('/ai/conversations', data, { skipRoleRouting: true })
    return response.conversation
  },

  /**
   * Send a follow-up message to continue the conversation
   * 
   * Flow:
   * 1. PUT with query and status: "input_received"
   * 2. Backend triggers Ai::WorkflowResumeJob
   * 3. AI continues with accumulated state
   * 4. WebSocket broadcasts updates to FE
   */
  continueConversation: async (id: number, query: string, extraPayload?: Record<string, unknown> | FormData): Promise<Conversation> => {
    if (extraPayload instanceof FormData) {
      // Detect whether caller is using nested `conversation[...]` keys; if so,
      // keep the auto-injected query/status under that namespace so Rails
      // strong params accept them. Falls back to flat keys for legacy callers.
      const usesNested = extraPayload.has('conversation[query]') ||
        extraPayload.has('conversation[status]') ||
        extraPayload.has('conversation[workflow]')
      if (usesNested) {
        if (!extraPayload.has('conversation[query]')) {
          extraPayload.append('conversation[query]', query)
        }
        if (!extraPayload.has('conversation[status]')) {
          extraPayload.append('conversation[status]', 'input_received')
        }
      } else {
        if (!extraPayload.has('query')) extraPayload.append('query', query)
        if (!extraPayload.has('status')) extraPayload.append('status', 'input_received')
      }
      const response = await apiClient.putFormData<ConversationResponse>(`/ai/conversations/${id}`, extraPayload, { skipRoleRouting: true })
      return response.conversation
    }
    const response = await apiClient.put<ConversationResponse>(
      `/ai/conversations/${id}`,
      { query, status: 'input_received', ...extraPayload },
      { skipRoleRouting: true }
    )
    return response.conversation
  },

  /**
   * Update an existing conversation (legacy/admin use)
   */
  updateConversation: async (id: number, data: UpdateConversationRequest): Promise<Conversation> => {
    const response = await apiClient.patch<ConversationResponse>(
      `/ai/conversations/${id}`,
      { conversation: data },
      { skipRoleRouting: true }
    )
    return response.conversation
  },

  /**
   * Delete a conversation
   */
  deleteConversation: async (id: number): Promise<void> => {
    await apiClient.delete(
      `/ai/conversations/${id}`,
      { skipRoleRouting: true }
    )
  },

  /**
   * Create ad units from selected templates
   * 
   * Flow:
   * 1. PUT with selected_templates and status: "input_received"
   * 2. Backend triggers ad unit creation workflow
   * 3. WebSocket broadcasts preview_creative_update with created ad units
   * 
   * @param id - Conversation ID
   * @param selectedTemplates - Array of templates with content_type and sizes
   * @returns Updated conversation
   */
  createAdUnits: async (
    id: number,
    selectedTemplates: SelectedTemplate[],
    assets?: File[]
  ): Promise<Conversation> => {
    if (assets && assets.length > 0) {
      const fd = new FormData()
      fd.append('conversation[query]', 'create ad unit')
      fd.append('conversation[status]', 'input_received')
      selectedTemplates.forEach((t, i) => {
        fd.append(`conversation[selected_templates][${i}][content_type]`, t.content_type)
        t.sizes.forEach(s =>
          fd.append(`conversation[selected_templates][${i}][sizes][]`, s)
        )
      })
      assets.forEach(f => fd.append('assets[]', f, f.name))
      const response = await apiClient.putFormData<ConversationResponse>(
        `/ai/conversations/${id}`,
        fd,
        { skipRoleRouting: true }
      )
      return response.conversation
    }
    const response = await apiClient.put<ConversationResponse>(
      `/ai/conversations/${id}`,
      {
        conversation: {
          query: 'create ad unit',
          status: 'input_received',
          selected_templates: selectedTemplates
        }
      },
      { skipRoleRouting: true }
    )
    return response.conversation
  },

  /**
   * Create media buy from selected products
   * 
   * Flow:
   * 1. PUT with products and query: "create_media_buy"
   * 2. Backend triggers media buy creation workflow
   * 3. WebSocket broadcasts response for further processing
   * 
   * @param id - Conversation ID
   * @param products - Array of selected SalesProduct objects
   * @returns Updated conversation
   */
  createMediaBuy: async (
    id: number,
    products: SalesProduct[],
    extras: {
      campaign_id?: number | string
      workflow?: string
      domain?: string
      brand_id?: string
      budget?: string | number
      pricing_option_id?: string
    } = {},
  ): Promise<Conversation> => {
    const payload: Record<string, unknown> = {
      products,
      query: 'create_media_buy',
    }
    // Only include the extra fields the caller actually provided so we don't
    // accidentally overwrite backend defaults with `undefined` values.
    for (const [key, value] of Object.entries(extras)) {
      if (value === undefined || value === null || value === '') continue
      payload[key] = value
    }
    const response = await apiClient.put<ConversationResponse>(
      `/ai/conversations/${id}`,
      payload,
      { skipRoleRouting: true },
    )
    return response.conversation
  },

  /**
   * Sync creatives with media buy
   * 
   * Flow:
   * 1. PUT with sync_creatives params from media buy response
   * 2. Backend triggers creative sync workflow
   * 3. WebSocket broadcasts response with synced creative data
   * 
   * Payload structure:
   * {
   *   "query": "sync_creatives",
   *   "status": "input_received",
   *   "selected_templates": [{ "content_type": "card_swiper", "sizes": ["300x600"] }],
   *   "creatives": [{ "creative_id": "...", "name": "...", "format_id": {...}, ... }],
   *   "assignments": [{ "creative_id": "...", "package_id": "..." }, ...]
   * }
   * 
   * @param id - Conversation ID
   * @param syncData - Sync creatives request data
   * @returns Updated conversation
   */
  syncCreatives: async (
    id: number,
    syncData: SyncCreativesRequest,
    extras: {
      campaign_id?: number | string
      workflow?: string
    } = {},
  ): Promise<Conversation> => {
    const payload: Record<string, unknown> = {
      query: 'sync_creatives',
      status: 'input_received',
      selected_templates: syncData.selected_templates,
      creatives: syncData.creatives,
      assignments: syncData.assignments,
    }
    // Only include the extra fields the caller actually provided so we don't
    // overwrite backend defaults with undefined values.
    for (const [key, value] of Object.entries(extras)) {
      if (value === undefined || value === null || value === '') continue
      payload[key] = value
    }
    const response = await apiClient.put<ConversationResponse>(
      `/ai/conversations/${id}`,
      payload,
      { skipRoleRouting: true },
    )
    return response.conversation
  },

}

